import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Megaphone, UsersRound } from "lucide-react";
import { ApiError } from "@/lib/api";
import { useAccounts } from "@/lib/accounts";
import {
  LIMITS,
  type OwnChatKind,
  accountOptionLabel,
  canAct,
  useCreateOwnChat,
} from "@/lib/ownChats";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { cn } from "@/lib/cn";

export const TEXTAREA =
  "w-full rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm leading-relaxed " +
  "placeholder:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

/** One of two ways, as a card to click. */
export function Choice({
  active,
  onClick,
  icon,
  title,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  icon?: ReactNode;
  title: ReactNode;
  hint: ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        "flex-1 rounded-xl border px-3 py-2.5 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        active ? "border-accent bg-accent/10" : "border-border hover:border-fg-muted/50",
      )}
    >
      <span className="flex items-center gap-2 text-sm font-medium text-fg-primary">
        {icon}
        {title}
      </span>
      <span className="mt-1 block text-xs leading-relaxed text-fg-muted">{hint}</span>
    </button>
  );
}

/**
 * A new group or channel, created from one account: which account, what kind,
 * what it is called, and whether anybody can find it or only people with the
 * link. An account that has not finished its warmup may still be used — after
 * the operator has read what that risks and said so.
 */
export function CreateOwnChatDialog({
  open,
  onOpenChange,
  accountId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The account to start with — set when the dialog is opened from its card. */
  accountId: string | null;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const accounts = useAccounts();
  const create = useCreateOwnChat();
  const [account, setAccount] = useState("");
  const [kind, setKind] = useState<OwnChatKind>("channel");
  const [title, setTitle] = useState("");
  const [about, setAbout] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [riskAccepted, setRiskAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const usable = useMemo(
    () => (accounts.data ?? []).filter(canAct).sort((a, b) => a.seq_no - b.seq_no),
    [accounts.data],
  );
  const chosen = usable.find((a) => a.id === account);

  // Every opening starts clean, on the account it was opened for.
  useEffect(() => {
    if (!open) return;
    setAccount(accountId ?? "");
    setKind("channel");
    setTitle("");
    setAbout("");
    setIsOpen(false);
    setUsername("");
    setRiskAccepted(false);
    setError(null);
  }, [open, accountId]);

  const name = username.trim().replace(/^@/, "");
  const ready =
    chosen !== undefined &&
    title.trim() !== "" &&
    (!isOpen || name !== "") &&
    (chosen.warmup_completed || riskAccepted);

  const submit = async () => {
    if (!chosen) return;
    setError(null);
    try {
      const created = await create.mutateAsync({
        account_id: chosen.id,
        kind,
        title: title.trim(),
        about: about.trim(),
        username: isOpen ? name : null,
      });
      pushToast(
        created.warning
          ? {
              kind: "info",
              text: t("own_chats.create.done_partly"),
              detail: created.warning,
            }
          : { kind: "success", text: t(`own_chats.create.done_${kind}`) },
      );
      onOpenChange(false);
      onCreated(created.chat.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("own_chats.action_error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next || !create.isPending) && onOpenChange(next)}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("own_chats.create.title")}</DialogTitle>
          <DialogDescription>{t("own_chats.create.subtitle")}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <div className="space-y-2">
            <Label>{t("own_chats.field.account")}</Label>
            <SimpleSelect
              value={chosen ? account : ""}
              onValueChange={(v) => {
                setAccount(v);
                setRiskAccepted(false);
              }}
              placeholder={t("own_chats.field.account_placeholder") ?? undefined}
              disabled={accounts.isLoading}
              options={usable.map((a) => ({
                value: a.id,
                label: accountOptionLabel(a, t("own_chats.account_not_warmed")),
              }))}
            />
            {accounts.data && usable.length === 0 ? (
              <p className="text-xs text-danger">{t("own_chats.create.no_accounts")}</p>
            ) : (
              accounts.data &&
              account !== "" &&
              !chosen && (
                <p className="text-xs text-warning">{t("own_chats.account_cannot_act")}</p>
              )
            )}
          </div>

          <div className="space-y-2">
            <Label>{t("own_chats.field.kind")}</Label>
            <div
              role="radiogroup"
              aria-label={t("own_chats.field.kind")}
              className="flex flex-col gap-2 sm:flex-row"
            >
              <Choice
                active={kind === "channel"}
                onClick={() => setKind("channel")}
                icon={<Megaphone size={15} strokeWidth={2} aria-hidden="true" />}
                title={t("own_chats.kind.channel")}
                hint={t("own_chats.kind.channel_hint")}
              />
              <Choice
                active={kind === "group"}
                onClick={() => setKind("group")}
                icon={<UsersRound size={15} strokeWidth={2} aria-hidden="true" />}
                title={t("own_chats.kind.group")}
                hint={t("own_chats.kind.group_hint")}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="oc_title">{t("own_chats.field.title")}</Label>
            <Input
              id="oc_title"
              value={title}
              maxLength={LIMITS.title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("own_chats.field.title_placeholder") ?? ""}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="oc_about">{t("own_chats.field.about")}</Label>
            <textarea
              id="oc_about"
              value={about}
              maxLength={LIMITS.about}
              rows={3}
              onChange={(e) => setAbout(e.target.value)}
              placeholder={t("own_chats.field.about_placeholder") ?? ""}
              className={cn(TEXTAREA, "resize-y")}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("own_chats.field.access")}</Label>
            <div
              role="radiogroup"
              aria-label={t("own_chats.field.access")}
              className="flex flex-col gap-2 sm:flex-row"
            >
              <Choice
                active={!isOpen}
                onClick={() => setIsOpen(false)}
                title={t("own_chats.access.closed")}
                hint={t("own_chats.access.closed_hint")}
              />
              <Choice
                active={isOpen}
                onClick={() => setIsOpen(true)}
                title={t("own_chats.access.open")}
                hint={t("own_chats.access.open_hint")}
              />
            </div>
            {isOpen && <UsernameField id="oc_username" value={username} onChange={setUsername} />}
          </div>

          {chosen && !chosen.warmup_completed && (
            <Alert tone="warning" title={t("own_chats.create.risk_title")}>
              <p>{t("own_chats.create.risk_text")}</p>
              <label className="mt-2 flex cursor-pointer items-center gap-2 text-fg-primary">
                <Checkbox
                  checked={riskAccepted}
                  onCheckedChange={(v) => setRiskAccepted(v === true)}
                />
                {t("own_chats.create.risk_accept")}
              </label>
            </Alert>
          )}

          {error && <Alert tone="danger">{error}</Alert>}
        </DialogBody>

        <DialogFooter>
          {create.isPending && (
            <span className="mr-auto text-xs text-fg-muted">{t("own_chats.working")}</span>
          )}
          <Button
            variant="outline"
            disabled={create.isPending}
            onClick={() => onOpenChange(false)}
          >
            {t("common.cancel")}
          </Button>
          <Button disabled={!ready || create.isPending} loading={create.isPending} onClick={submit}>
            {t("own_chats.create.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
CreateOwnChatDialog.displayName = "CreateOwnChatDialog";

/** A public name, typed after the "t.me/" it will live under. */
export function UsernameField({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <div className="flex items-center rounded-lg border border-border bg-bg-primary focus-within:ring-2 focus-within:ring-accent">
        <span className="pl-3 font-mono text-sm text-fg-muted">t.me/</span>
        <input
          id={id}
          value={value}
          disabled={disabled}
          maxLength={32}
          onChange={(e) =>
            onChange(
              e.target.value
                .replace(/^\s*(?:https?:\/\/)?(?:t|telegram)\.me\//i, "")
                .replace(/[^A-Za-z0-9_@]/g, ""),
            )
          }
          aria-label={t("own_chats.field.username")}
          placeholder="my_channel"
          className="min-w-0 flex-1 bg-transparent py-2 pr-3 font-mono text-sm focus-visible:outline-none disabled:opacity-60"
        />
      </div>
      <p className="text-xs text-fg-muted">{t("own_chats.field.username_hint")}</p>
    </div>
  );
}
